import React from 'react';
import { useFormikContext } from 'formik';
import { Typography } from '@mui/material';
import { AcceptRequestFlowValues } from '../../../types/appointments';
import { getSchedulingModeOptions } from '../../../utils/form/acceptRequestFlow';
import {
  getLengthOptions,
  getTakenSessionSlots,
  updateSessionStart,
} from '../../../helpers/appointments';
import { InputRowType, LoadedUser } from '../../../interfaces';
import useUser from '../../../hooks/global/useUser';
import InputGroup from '../InputGroup';
import SelectInput from '../SelectInput';
import CalendarInput, { getCalendarInputName } from '../CalendarInput';
import OfferTimesInput from '../OfferTimesInput';

interface Props {
  clientFirstName: string;
}

function SchedulingChoice({ clientFirstName }: Props) {
  const { values, setFieldValue, errors } =
    useFormikContext<AcceptRequestFlowValues>();
  const { user: loadedUser } = useUser<LoadedUser>();

  const isPickTime = values.schedulingMode === 'PICK_TIME';
  const isOfferTimes = values.schedulingMode === 'OFFER_TIMES';
  const isConsultation = values.requestKind === 'CONSULTATION';
  // Multi-session projects can't be offered a single set of times
  const allowOfferTimes = isConsultation || (values.numberOfSessions || 1) <= 1;

  const sessionLengthOptions = React.useMemo(
    () =>
      (
        getLengthOptions({
          user: loadedUser,
          appointmentType: values.requestKind,
          serviceId: values.serviceId ? Number(values.serviceId) : undefined,
          businessId: values.businessId || undefined,
        }) || []
      ).map(option => ({
        value: String(option.value),
        label: String(option.label),
      })),
    [loadedUser, values.businessId, values.requestKind, values.serviceId],
  );

  // In pick-a-time mode every session needs its own calendar; keep the
  // extra-session list in step with the session count without dropping
  // any dates the user already picked
  React.useEffect(() => {
    if (!isPickTime || isConsultation) return;
    const targetCount = Math.max(0, (values.numberOfSessions || 1) - 1);
    const current = values.additionalAppointments || [];
    if (current.length === targetCount) return;

    const next = current.slice(0, targetCount);
    while (next.length < targetCount) {
      next.push({
        appointmentLengthInMinutes: values.appointmentLengthInMinutes || '',
        startDateTime: '',
        endDateTime: '',
      });
    }
    setFieldValue('additionalAppointments', next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPickTime, isConsultation, values.numberOfSessions]);

  // A changed main duration always invalidates the picked main date, no
  // matter which input changed it (inline row, Apt. Length, snap-to-max)
  const prevMainLength = React.useRef(values.appointmentLengthInMinutes);
  React.useEffect(() => {
    if (prevMainLength.current === values.appointmentLengthInMinutes) return;
    prevMainLength.current = values.appointmentLengthInMinutes;
    if (values.startDateTime) {
      setFieldValue('startDateTime', '');
      setFieldValue('endDateTime', '');
    }
    if (values.offeredSlots?.length) {
      setFieldValue('offeredSlots', []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.appointmentLengthInMinutes]);

  // A different artist, service or trip changes what's bookable
  const prevSlotContext = React.useRef(
    `${values.employeeId}-${values.serviceId}-${values.businessTripId}`,
  );
  React.useEffect(() => {
    const context = `${values.employeeId}-${values.serviceId}-${values.businessTripId}`;
    if (prevSlotContext.current === context) return;
    prevSlotContext.current = context;
    if (values.offeredSlots?.length) setFieldValue('offeredSlots', []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.employeeId, values.serviceId, values.businessTripId]);

  // The option is hidden once the session count goes up, so correct a
  // selection that was made before it did
  React.useEffect(() => {
    if (allowOfferTimes) return;
    if (isOfferTimes) setFieldValue('schedulingMode', 'BOOKING_LINK');
    if (values.offeredSlots?.length) setFieldValue('offeredSlots', []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowOfferTimes, isOfferTimes]);

  React.useEffect(() => {
    if (isOfferTimes || !values.offeredSlots?.length) return;
    setFieldValue('offeredSlots', []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOfferTimes]);

  const onChangeMainSession = (value: Date) => {
    setFieldValue('startDateTime', value.toISOString());
  };

  const onChangeSession = (index: number, value: Date) => {
    const next = updateSessionStart(
      values.additionalAppointments || [],
      index,
      value,
      values.appointmentLengthInMinutes,
    );
    if (next) setFieldValue('additionalAppointments', next);
  };

  const onChangeSessionDuration = (index: number, value: string) => {
    const additionalAppointments = [...(values.additionalAppointments || [])];
    const session = additionalAppointments[index];
    if (!session || session.appointmentLengthInMinutes === value) return;
    additionalAppointments[index] = {
      ...session,
      appointmentLengthInMinutes: value,
      // A different duration invalidates the picked slot — re-pick the date
      startDateTime: '',
      endDateTime: '',
    };
    setFieldValue('additionalAppointments', additionalAppointments);
  };

  // Per-session duration; index -1 edits the main appointmentLengthInMinutes
  // (the Appointment Details length input stays as the flow-wide default)
  const getDurationRow = (index: number) => {
    const isMain = index < 0;
    const name = isMain
      ? 'appointmentLengthInMinutes'
      : `additionalAppointments.${index}.appointmentLengthInMinutes`;
    const session = isMain ? undefined : values.additionalAppointments?.[index];
    return [
      {
        name,
        label: '',
        type: 'node',
        node: (
          <SelectInput
            // eslint-disable-next-line react/no-array-index-key
            key={`${values.employeeId}-accept-request-length-${index}`}
            name={name}
            label={`Session ${index + 2} length`}
            options={sessionLengthOptions}
            placeholder="Select a length"
            values={{
              [name]: String(
                (isMain
                  ? values.appointmentLengthInMinutes
                  : session?.appointmentLengthInMinutes) || '',
              ),
            }}
            setFieldValue={(_field, value) =>
              isMain
                ? setFieldValue('appointmentLengthInMinutes', String(value))
                : onChangeSessionDuration(index, String(value))
            }
          />
        ),
      },
    ];
  };

  // One calendar row per session; index -1 is the main session, which
  // lives in startDateTime rather than additionalAppointments
  const getCalendarRow = (index: number) => {
    const session =
      index >= 0 ? values.additionalAppointments?.[index] : undefined;
    return [
      {
        name: session ? getCalendarInputName(index) : 'startDateTime',
        label: '',
        type: 'node',
        node: (
          <CalendarInput
            name={session ? getCalendarInputName(index) : undefined}
            // eslint-disable-next-line react/no-array-index-key
            key={`${values.employeeId}-accept-request-${index}`}
            error={
              session
                ? // @ts-expect-error Formik types nested array errors loosely
                  errors?.additionalAppointments?.[index]?.startDateTime
                : errors?.startDateTime
            }
            appointmentLengthInMinutes={
              session?.appointmentLengthInMinutes ||
              values.appointmentLengthInMinutes
            }
            startDateTime={
              (session ? session.startDateTime : values.startDateTime) || ''
            }
            onChange={value =>
              session
                ? onChangeSession(index, value)
                : onChangeMainSession(value)
            }
            onDelete={undefined}
            takenSlots={getTakenSessionSlots(index, values)}
          />
        ),
      },
    ];
  };

  const inputRows = [
    [
      {
        name: 'schedulingMode',
        label: 'Who picks the time? *',
        type: 'select',
        options: getSchedulingModeOptions(clientFirstName, { allowOfferTimes }),
        placeholder: 'Select an option',
      },
    ],
    values.schedulingMode === 'BOOKING_LINK' && [
      {
        name: 'schedulingMode.helper',
        label: '',
        type: 'node',
        node: (
          <Typography variant="body2" color="text.secondary" m={1.5}>
            We&apos;ll text {clientFirstName} a link to pick a time
            {isConsultation ? '' : ' and pay the deposit'}.
          </Typography>
        ),
      },
    ],
    isOfferTimes && [
      {
        name: 'offeredSlots',
        label: '',
        type: 'node',
        node: (
          <OfferTimesInput
            key={`${values.employeeId}-offer-times-input`}
            appointmentLengthInMinutes={values.appointmentLengthInMinutes}
            offeredSlots={values.offeredSlots || []}
            onChange={starts => setFieldValue('offeredSlots', starts)}
            helperText={`We'll text ${clientFirstName} the times you pick${
              isConsultation ? '' : ' and a link to pay the deposit'
            }. They choose one.`}
            error={
              typeof errors.offeredSlots === 'string'
                ? errors.offeredSlots
                : undefined
            }
          />
        ),
      },
    ],
    // Duration + calendar share a row so each session reads as one line; the
    // main session only gets its inline duration when there are others
    isPickTime &&
      (!isConsultation && (values.additionalAppointments || []).length
        ? [...getDurationRow(-1), ...getCalendarRow(-1)]
        : getCalendarRow(-1)),
    ...(isPickTime && !isConsultation
      ? (values.additionalAppointments || []).map((_, index) => [
          ...getDurationRow(index),
          ...getCalendarRow(index),
        ])
      : []),
  ].filter(Boolean) as InputRowType[];

  return <InputGroup name="Scheduling" rows={inputRows} />;
}

export default SchedulingChoice;
